// @flow
import React from 'react';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { EntityRoutes } from 'routes/constants';
import strings from 'strings';
import { KeywordTags } from 'UI/components/molecules/KeywordTags';
import { ProfileAlert } from 'UI/components/molecules/ProfileAlert';
import { EntityType } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { nestTernary, phoneNumberFormatter } from 'UI/utils';
import { buildLocationInfoLabel } from 'UI/utils/inventory';

import InfoLabelsGrid from '../InfoLabelsGrid';
import InventoryOverviewLayout from '../InventoryOverviewLayout';
import {
  buildEmailLabels,
  buildKeywordTagsCard,
  extractFullName,
  getEmailValidationAlerts,
  getPhonesLabelsForProfile
} from '../utils';

const {
  shared: {
    fields: { currentCompany: currentCompanyString }
  }
} = strings;

type ContactOverviewProps = {
  profile: Object,
  loading: boolean
};

const ContactOverview = ({ loading, profile }: ContactOverviewProps) => {
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const shouldShowKeywordTags = checkIfFeatureFlagEnabled(FeatureFlags.ContactKeywordTags);

  const country = profile?.personalInformation?.address?.city?.state?.country?.slug;
  const state = profile?.personalInformation?.address?.city?.state?.slug;

  const hasMultiplePhones = !!profile.phone_id;
  const showMultiplePhones = checkIfFeatureFlagEnabled(FeatureFlags.ContactsMultiplePhones);

  const fullName = extractFullName(profile, EntityType.Contact);

  const legacyPhoneLabels = [
    {
      title: 'Phone',
      mode: 'ringCentral',
      linkProps: {
        url: phoneNumberFormatter(profile?.personalInformation?.contact?.phone) ?? '',
        id: profile?.id,
        role: EntityType.Contact,
        customLinkProps: {
          profileName: fullName
        }
      }
    },
    {
      title: 'Ext',
      description: profile?.personalInformation?.contact?.ext
    },
    {
      title: 'Other Phone',
      mode: 'ringCentral',
      linkProps: {
        url: phoneNumberFormatter(profile?.personalInformation?.contact?.mobile) ?? '',
        id: profile?.id,
        role: EntityType.Contact,
        customLinkProps: {
          profileName: fullName
        }
      }
    }
  ];
  const generalLabels = [
    {
      title: 'Nickname',
      description: profile?.personalInformation?.nickname
    },
    {
      title: 'Title',
      description: profile?.title
    },
    {
      title: strings.shared.fields.jobFunction,
      description: profile?.position?.title
    },
    {
      title: 'Industry: Specialty',
      description: `${profile?.specialty?.industry?.title}: ${profile?.specialty?.title}`
    },
    { title: 'Subspecialty', description: profile?.subspecialty?.title },
    {
      title: currentCompanyString,
      mode: profile?.company_id ? 'localLink' : 'text',
      description: profile?.current_company,
      itemId: profile?.company_id,
      linkProps: profile?.company_id
        ? {
            url: EntityRoutes.CompanyProfile
          }
        : ''
    },
    buildLocationInfoLabel({
      city: profile?.personalInformation?.address?.city?.title,
      state,
      country
    }),
    {
      title: 'Zip Code',
      description: profile?.personalInformation?.address?.zip
    },
    {
      title: 'Source',
      description: profile?.sourceType?.title
    },
    {
      title: 'Linkedin Profile',
      mode: 'externalLink',
      linkProps: {
        url: profile?.link_profile ?? ''
      }
    },
    ...buildEmailLabels({ profile, entityType: EntityType.Contact })
  ];

  const phonesLabels = !showMultiplePhones
    ? legacyPhoneLabels
    : nestTernary(
        hasMultiplePhones,
        getPhonesLabelsForProfile(profile, EntityType.Contact),
        legacyPhoneLabels
      );

  const alerts = getEmailValidationAlerts({
    profile,
    loading
  }).map(item => ({
    componentProps: item,
    Component: ProfileAlert
  }));

  const cards = [
    ...alerts,
    ...buildKeywordTagsCard({
      Component: KeywordTags,
      keywordTags: profile?.keywordTags,
      isEnabled: shouldShowKeywordTags
    }),
    {
      cardProps: { headerProps: { title: 'General Information' } },
      Component: InfoLabelsGrid,
      componentProps: { labels: [...generalLabels, ...phonesLabels] },
      className: 'generalCard'
    }
  ];

  return <InventoryOverviewLayout loading={loading} items={cards} />;
};

export default ContactOverview;
